import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Box, Button, Checkbox, Dialog, Flex, Grid, Select, Text, TextField } from '@radix-ui/themes'
import { Ops } from '@willie/schemas'
import { FieldShell } from '~/components/FieldShell'
import { LOGGER_MAKER_OPTIONS, loggerMakerLabel } from '~/features/installations/constants'
import { useMeterMakerCatalog, withSelectedMakers } from '~/features/installations/useMeterMakers'
import { useMeterModels } from '~/features/installations/useMeterModels'
import { useTRPC } from '~/utils/trpc'
import type { InstallationProfileRow } from '../assembly'
import { AssemblyProfileSection } from './AssemblyProfileSection'

type InstallationProfileDraft = Ops.InstallationProfileCatalog.InstallationProfileInput

const NO_LOGGER_MAKER = '__none__'

const emptyProfileDraft = (): InstallationProfileDraft => ({
  slug: '',
  meterMakers: [],
  meterModels: [],
  loggerMakers: [],
  supported: false
})

const toProfileDraft = (profile: InstallationProfileRow): InstallationProfileDraft => {
  const loggerMaker = Ops.InstallationProfileCatalog.profileLoggerMaker(profile.logger_makers)
  return {
    slug: profile.slug,
    meterMakers: profile.meter_makers as InstallationProfileDraft['meterMakers'],
    meterModels: profile.meter_models,
    loggerMakers: loggerMaker ? [loggerMaker] : [],
    supported: profile.supported
  }
}

interface InstallationProfileDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  profile: InstallationProfileRow | null
}

// Mount with a fresh `key` per opening: the draft is seeded from `profile` only on mount.
export const InstallationProfileDialog = ({ open, onOpenChange, profile }: InstallationProfileDialogProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<InstallationProfileDraft>(() =>
    profile ? toProfileDraft(profile) : emptyProfileDraft()
  )
  const [validationError, setValidationError] = useState<string | null>(null)

  const meterMakerCatalog = useMeterMakerCatalog()
  const meterModelsQuery = useMeterModels(draft.meterMakers, open)
  const invalidateProfiles = () =>
    queryClient.invalidateQueries({ queryKey: trpc.ops.installationProfiles.list.queryKey() })
  const createMutation = useMutation(
    trpc.ops.installationProfiles.create.mutationOptions({ onSuccess: invalidateProfiles })
  )
  const updateMutation = useMutation(
    trpc.ops.installationProfiles.update.mutationOptions({
      onSuccess: () =>
        Promise.all([
          invalidateProfiles(),
          queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.pathKey() })
        ])
    })
  )

  const saveProfile = async () => {
    const parsed = Ops.InstallationProfileCatalog.installationProfileInputSchema.safeParse(draft)
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Check the profile fields.')
      return
    }
    setValidationError(null)
    try {
      if (profile) {
        await updateMutation.mutateAsync(parsed.data)
      } else {
        await createMutation.mutateAsync(parsed.data)
      }
      onOpenChange(false)
    } catch {
      // Keep the dialog open; the mutation error is displayed below.
    }
  }

  const pending = createMutation.isPending || updateMutation.isPending
  const errorMessage = validationError ?? createMutation.error?.message ?? updateMutation.error?.message ?? null

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="680px">
        <Dialog.Title>{profile ? 'Edit installation profile' : 'New installation profile'}</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          All meter models do not restrict suggestions; empty maker groups remain unknown.
        </Dialog.Description>

        <Flex direction="column" gap="4" mt="4">
          <Box>
            <Text as="label" size="1" color="gray" className="block mb-1">
              Slug
            </Text>
            <TextField.Root
              value={draft.slug}
              disabled={profile !== null}
              placeholder="e.g. itron-new-adapter"
              onChange={(event) => setDraft((current) => ({ ...current, slug: event.target.value }))}
            />
            <Text size="1" color="gray" className="block mt-1">
              Used in guide URLs. Lowercase letters, numbers and hyphens only; it cannot be changed later.
            </Text>
          </Box>

          <Box>
            <Text size="1" color="gray" className="block mb-2">
              Meter makers
            </Text>
            <Grid columns="3" gap="2">
              {withSelectedMakers(meterMakerCatalog, draft.meterMakers).map(({ slug: maker, label }) => (
                <Text as="label" size="2" key={maker}>
                  <Flex align="center" gap="2">
                    <Checkbox
                      checked={draft.meterMakers.includes(maker)}
                      onCheckedChange={() =>
                        setDraft((current) => {
                          const meterMakers = current.meterMakers.includes(maker)
                            ? current.meterMakers.filter((value) => value !== maker)
                            : [...current.meterMakers, maker]
                          return { ...current, meterMakers, meterModels: [] }
                        })
                      }
                    />
                    {label}
                  </Flex>
                </Text>
              ))}
            </Grid>
          </Box>

          <Box>
            <Text size="1" color="gray" className="block mb-2">
              Meter models
            </Text>
            <Grid columns="3" gap="2">
              <Text as="label" size="2">
                <Flex align="center" gap="2">
                  <Checkbox
                    checked={draft.meterModels.length === 0}
                    onCheckedChange={(checked) => {
                      if (checked === true) setDraft((current) => ({ ...current, meterModels: [] }))
                    }}
                  />
                  All
                </Flex>
              </Text>
              {(meterModelsQuery.data ?? []).map((model) => (
                <Text as="label" size="2" key={model}>
                  <Flex align="center" gap="2">
                    <Checkbox
                      checked={draft.meterModels.includes(model)}
                      onCheckedChange={(checked) =>
                        setDraft((current) => ({
                          ...current,
                          meterModels:
                            checked === true
                              ? [...current.meterModels, model]
                              : current.meterModels.filter((value) => value !== model)
                        }))
                      }
                    />
                    {model}
                  </Flex>
                </Text>
              ))}
            </Grid>
            {draft.meterMakers.length === 0 ? (
              <Text size="1" color="gray" className="block mt-1">
                Select at least one meter maker to list models.
              </Text>
            ) : meterModelsQuery.isLoading ? (
              <Text size="1" color="gray" className="block mt-1">
                Loading meter models…
              </Text>
            ) : meterModelsQuery.error ? (
              <Text size="1" color="red" className="block mt-1">
                Could not load meter models.
              </Text>
            ) : meterModelsQuery.data?.length === 0 ? (
              <Text size="1" color="gray" className="block mt-1">
                No meter models found for these makers.
              </Text>
            ) : null}
          </Box>

          <FieldShell
            label="Logger maker"
            hint={
              <Text size="1" color="gray">
                Assigned to an installation when its compatibility check picks this profile.
              </Text>
            }
          >
            <Select.Root
              value={draft.loggerMakers[0] ?? NO_LOGGER_MAKER}
              onValueChange={(value) => {
                const maker = Ops.StationInstallations.loggerMakerSchema.safeParse(value).data
                setDraft((current) => ({ ...current, loggerMakers: maker ? [maker] : [] }))
              }}
            >
              <Select.Trigger className="w-full" />
              <Select.Content>
                <Select.Item value={NO_LOGGER_MAKER}>Unknown</Select.Item>
                {LOGGER_MAKER_OPTIONS.map((maker) => (
                  <Select.Item key={maker} value={maker}>
                    {loggerMakerLabel[maker]}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </FieldShell>

          <Box>
            <Text as="label" size="2">
              <Flex align="center" gap="2">
                <Checkbox
                  checked={draft.supported}
                  onCheckedChange={(checked) =>
                    setDraft((current) => ({ ...current, supported: checked === true }))
                  }
                />
                Supported
              </Flex>
            </Text>
            <Text size="1" color="gray" className="block mt-1">
              Supported profiles are the ones we commit to installing.
            </Text>
          </Box>

          <AssemblyProfileSection profile={{ slug: draft.slug, logger_makers: draft.loggerMakers }} />
        </Flex>

        {errorMessage ? (
          <Text color="red" size="2" as="p" mt="3">
            {errorMessage}
          </Text>
        ) : null}

        <Flex gap="3" mt="5" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={pending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button loading={pending} disabled={pending} onClick={saveProfile}>
            {profile ? 'Save changes' : 'Create profile'}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
